/**
 * The one create-cycle form's pure parts (owner ruling #7): its values, its
 * validation, and how its values become the `createCycle` input. Kept free of
 * React and stores so every surface that renders `CreateCycleForm` submits
 * exactly the same thing, and so it is unit-testable.
 */
import type { CycleCadence, CycleFrequency, CycleMode, StrategyRef } from '@/types/prioritization/degapCycle';

export interface CreateCycleFormValues {
  name: string;
  description: string;
  /** yyyy-mm-dd or '' */
  startDate: string;
  /** yyyy-mm-dd or '' */
  endDate: string;
  cadenceMode: CycleMode;
  frequency: CycleFrequency;
  orgScopeNodeId: string;
  workflowId: string;
  /** Owner ruling #9 — '' means no default. */
  defaultTrackOwnerId: string;
  /** An existing strategy's id, or '' */
  strategyId: string;
  /** A strategy named by hand when none is picked. */
  manualStrategyName: string;
}

export const EMPTY_CREATE_CYCLE_VALUES: CreateCycleFormValues = {
  name: '',
  description: '',
  startDate: '',
  endDate: '',
  cadenceMode: 'episodic',
  frequency: 'quarterly',
  orgScopeNodeId: '',
  workflowId: '',
  defaultTrackOwnerId: '',
  strategyId: '',
  manualStrategyName: '',
};

export const CYCLE_FREQUENCY_OPTIONS: { value: CycleFrequency; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi-annually', label: 'Semi-annually' },
  { value: 'annually', label: 'Annually' },
];

export interface CreateCycleRules {
  /** The workspace has an org chart, so the cycle must start at a node of it. */
  orgScopeRequired: boolean;
  /** The configuration the cycle starts from requires an explicit workflow. */
  workflowRequired: boolean;
}

/** The first thing stopping the form from being submitted, or null. */
export function validateCreateCycleValues(values: CreateCycleFormValues, rules: CreateCycleRules): string | null {
  if (!values.name.trim()) return 'Give the cycle a name.';
  if (rules.orgScopeRequired && !values.orgScopeNodeId) return 'Choose the org level this cycle starts from.';
  if (rules.workflowRequired && !values.workflowId) {
    return 'This configuration needs a workflow chosen before the cycle is created.';
  }
  if (values.startDate && values.endDate && values.endDate < values.startDate) {
    return 'The end date is before the start date.';
  }
  return null;
}

export function buildCycleCadence(values: Pick<CreateCycleFormValues, 'cadenceMode' | 'frequency'>): CycleCadence {
  return {
    mode: values.cadenceMode,
    frequency: values.cadenceMode === 'periodic' ? values.frequency : undefined,
    indefinite: values.cadenceMode === 'continuous',
  };
}

export function buildStrategyRef(
  values: Pick<CreateCycleFormValues, 'strategyId' | 'manualStrategyName'>,
  strategies: ReadonlyArray<{ id: string; name: string; kind?: StrategyRef['kind'] }>,
): StrategyRef | undefined {
  if (values.strategyId) {
    const found = strategies.find((s) => s.id === values.strategyId);
    if (found) {
      // The kind travels with the link, so the cycle opens the right page.
      return found.kind
        ? { id: found.id, name: found.name, source: 'internal', kind: found.kind }
        : { id: found.id, name: found.name, source: 'internal' };
    }
  }
  const manual = values.manualStrategyName.trim();
  return manual ? { name: manual, source: 'manual' } : undefined;
}

/** What `useDegapProcessStore.createCycle` is called with. */
export interface CreateCycleInput {
  name: string;
  description?: string;
  createdBy: string;
  cadence: CycleCadence;
  orgScopeNodeId?: string;
  workflowId?: string;
  configurationId?: string;
  strategyRef?: StrategyRef;
  defaultTrackOwnerId?: string;
  dateRange?: { start: string; end: string };
  /** The cycle template chosen at creation; the server applies it once (mig 1040). */
  templateId?: string;
}

export function buildCreateCycleInput(
  values: CreateCycleFormValues,
  ctx: { userId: string; configurationId?: string | null; strategies: ReadonlyArray<{ id: string; name: string; kind?: StrategyRef['kind'] }> },
): CreateCycleInput {
  const input: CreateCycleInput = {
    name: values.name.trim(),
    createdBy: ctx.userId,
    cadence: buildCycleCadence(values),
  };
  const description = values.description.trim();
  if (description) input.description = description;
  if (values.orgScopeNodeId) input.orgScopeNodeId = values.orgScopeNodeId;
  if (values.workflowId) input.workflowId = values.workflowId;
  if (ctx.configurationId) input.configurationId = ctx.configurationId;
  const strategyRef = buildStrategyRef(values, ctx.strategies);
  if (strategyRef) input.strategyRef = strategyRef;
  if (values.defaultTrackOwnerId) input.defaultTrackOwnerId = values.defaultTrackOwnerId;
  if (values.startDate || values.endDate) input.dateRange = { start: values.startDate, end: values.endDate };
  return input;
}

/**
 * Where a new cycle opens. The form already collected setup, so the Designer
 * opens on Design Paths (its Setup tab stays clickable); without the Designer,
 * the setup wizard's first step.
 */
export function createdCycleDestination(cycleId: string, designerEnabled: boolean): string {
  return designerEnabled
    ? `/prioritization/cycles/${cycleId}/designer?step=2`
    : `/prioritization/cycles/${cycleId}/setup?step=1`;
}
